import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import { observer } from 'mobx-react';
import EyeCrossed16 from '@honeybook/icons/dist/icons/EyeCrossed16';
import Eye16 from '@honeybook/icons/dist/icons/Eye16';
import HBLink from '@honeybook/hbui/dist/components/HBLink/HBLink';
import useAnalytics from '~/common/useAnalytics';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import SetThumbnail from '~/common/SetThumbnail/SetThumbnail';
import { useRootStore } from '~/stores/useRootStore';

export const THUMBNAIL_CROP_RATIO = 16 / 10.5;

interface Props {
    updateConfigurationData: (mainKey, key, value) => void;
}

const SetThumbnailPreview = ({ updateConfigurationData }: Props) => {
    const {
        builderUiStore: { selectedFlow },
        companyStore: { currCompany }
    } = useRootStore();
    const [flowThumbnail, setFlowThumbnail] = useState(
        selectedFlow.thumbnail_image
    );
    const { track } = useAnalytics();

    useEffect(() => {
        if (selectedFlow.thumbnail_image !== flowThumbnail) {
            updateConfigurationData(
                'thumbnail',
                null,
                flowThumbnail
                    ? {
                          ...flowThumbnail
                      }
                    : null
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flowThumbnail]);

    const deleteThumbnail = () => {
        setFlowThumbnail(null);
    };

    return (
        <SetThumbnailContainer>
            <HBText type="Body5DarkSecondary">
                Choose a thumbnail to visually represent your file
            </HBText>
            <StyledSetThumbnail
                thumbnailImage={flowThumbnail}
                deleteThumbnail={deleteThumbnail}
                setThumbnailImage={setFlowThumbnail}
            />
            <VisibleToClientsContainer>
                {currCompany.is_flow_thumbnail_visible_to_client ? (
                    <Eye16 />
                ) : (
                    <EyeCrossed16 />
                )}
                <VisibleToClientsText type="Body6DarkSecondary">
                    {currCompany.is_flow_thumbnail_visible_to_client
                        ? 'Visible to clients.'
                        : 'Not visible to clients.'}{' '}
                    <EditPrefLink
                        onClick={() => {
                            const link =
                                '/app/settings/company/preferences#flow-settings';
                            track(AnalyticsEvents.FlowThumbnailSettings, {
                                action: 'click',
                                type: 'flow thumbnail'
                            });

                            window.location.href = link;
                        }}
                        textType="Body6DarkSecondary"
                        testIdName="edit-settings"
                    >
                        Edit preferences
                    </EditPrefLink>
                </VisibleToClientsText>
            </VisibleToClientsContainer>
        </SetThumbnailContainer>
    );
};

export default observer(SetThumbnailPreview);

// ----------------------styles-------------------

const SetThumbnailContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 40px;
`;

const EditPrefLink = styled(HBLink)`
    text-decoration: underline;
    cursor: pointer;
`;

const VisibleToClientsContainer = styled.div`
    display: flex;
    align-items: center;
`;

const VisibleToClientsText = styled(HBText)`
    margin-left: 8px;
    margin-right: 4px;
`;

const StyledSetThumbnail = styled(SetThumbnail)`
    margin-block: 12px 16px;
`;
